import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Platform,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { OverlayViewF, OVERLAY_MOUSE_TARGET } from '@react-google-maps/api';
import Icon from 'react-native-vector-icons/MaterialIcons';

import { useIconTheme } from '../contexts/IconThemeContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';
import WriteReviewModal from './WriteReviewModal';
import { getHotspotReviews } from '../services/database';
import { getProfilePictureUrl, getHotspotPhotoUrl } from '../services/storage';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const isDesktop = Platform.OS === 'web' && SCREEN_WIDTH >= 768;
const isMobile = SCREEN_WIDTH < 768;
const MOBILE_NAV_BAR_HEIGHT = 85;
const MOBILE_POPUP_VERTICAL_SPACING = 48; // space above and below popup (e.g. 24 top + 24 bottom)
const MOBILE_MAX_POPUP_HEIGHT_AVAILABLE = SCREEN_HEIGHT - MOBILE_NAV_BAR_HEIGHT - MOBILE_POPUP_VERTICAL_SPACING;
// Responsive popup: smaller on mobile so it doesn't get cut off
const POPUP_WIDTH = Platform.OS === 'web'
  ? (isMobile ? Math.min(340, SCREEN_WIDTH * 0.92) : Math.min(380, SCREEN_WIDTH * 0.9))
  : (isMobile ? Math.min(340, SCREEN_WIDTH * 0.92) : 380);
const MAX_POPUP_HEIGHT = Platform.OS === 'web'
  ? (isMobile ? Math.max(320, MOBILE_MAX_POPUP_HEIGHT_AVAILABLE) : Math.min(500, SCREEN_HEIGHT * 0.6))
  : (isMobile ? Math.max(320, MOBILE_MAX_POPUP_HEIGHT_AVAILABLE) : 500);

// Isolated photo strip: owns its own auto-rotate state so interval doesn't re-render the whole popup (avoids overlay position glitch)
const HotspotPopupPhotoStrip = React.memo(({ photos, isMobile, isDarkMode, colors }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  useEffect(() => {
    if (photos?.length > 1) {
      const interval = setInterval(() => {
        setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [photos?.length]);
  useEffect(() => {
    setCurrentPhotoIndex(0);
  }, [photos]);
  if (!photos || photos.length === 0) return null;
  return (
    <div style={{ position: 'relative', height: isMobile ? '120px' : '180px', minHeight: isMobile ? 100 : 150, overflow: 'hidden', borderRadius: '20px 20px 0 0' }}>
      <div
        style={{
          display: 'flex',
          width: `${photos.length * 100}%`,
          height: '100%',
          transform: `translateX(-${currentPhotoIndex * (100 / photos.length)}%)`,
          transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        {photos.map((photo, index) => (
          <img
            key={index}
            src={photo}
            style={{
              width: `${100 / photos.length}%`,
              height: '100%',
              objectFit: 'cover',
              flexShrink: 0,
            }}
            alt=""
          />
        ))}
      </div>
      {photos.length > 1 && (
        <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '6px' }}>
          {photos.map((_, index) => (
            <div
              key={index}
              style={{
                width: index === currentPhotoIndex ? '20px' : '8px',
                height: '8px',
                borderRadius: '4px',
                backgroundColor: index === currentPhotoIndex ? (isDarkMode ? colors.text : '#fff') : (isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.5)'),
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onClick={() => setCurrentPhotoIndex(index)}
              role="button"
              aria-label={`Photo ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
});

const HotspotPopup = ({ hotspot, visible, onClose, nearbyPlayers, onPlayerClick, mapRef, currentUserDbId, navigation, onShowDetail, isMapDragging, frozenPopupViewport }) => {
  const { getCategoryIcon } = useIconTheme();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [avgRating, setAvgRating] = useState(0);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  // Auto-rotate reviews every 4 seconds (state kept here so review section can stay in main content; only this index updates, minimal re-render impact)
  useEffect(() => {
    if (visible && reviews.length > 1) {
      const interval = setInterval(() => {
        setCurrentReviewIndex((prev) => (prev + 1) % reviews.length);
      }, 4000);
      return () => clearInterval(interval);
    }
    if (!visible) setCurrentReviewIndex(0);
  }, [visible, reviews.length]);

  useEffect(() => {
    if (visible && hotspot) {
      fetchReviews();
    }
  }, [visible, hotspot]);

  const fetchReviews = async () => {
    try {
      setLoadingReviews(true);
      
      const fetchedReviews = await getHotspotReviews(hotspot.id, 3);
      
      // Load S3 URLs for profile pictures
      const reviewsWithUrls = await Promise.all(
        fetchedReviews.map(async (review) => {
          if (review.user?.profilePictureUrl && !review.user.profilePictureUrl.startsWith('http')) {
            try {
              const url = await getProfilePictureUrl(review.user.profilePictureUrl);
              return {
                ...review,
                user: {
                  ...review.user,
                  profilePictureUrl: url,
                },
              };
            } catch (error) {
              console.error('Error loading profile picture for review:', error);
              return review;
            }
          }
          return review;
        })
      );
      
      setReviews(reviewsWithUrls);
      
      // Calculate average rating
      if (reviewsWithUrls.length > 0) {
        const avg = reviewsWithUrls.reduce((sum, r) => sum + r.rating, 0) / reviewsWithUrls.length;
        setAvgRating(avg);
      } else {
        setAvgRating(0);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviews([]);
      setAvgRating(0);
    } finally {
      setLoadingReviews(false);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Icon
          key={i}
          name={i <= rating ? 'star' : 'star-border'}
          size={16}
          color="#FFD700"
        />
      );
    }
    return stars;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      PARK: 'Park',
      CAFE: 'Cafe',
      BAR: 'Bar',
      CLUB: 'Chess Club',
      UNIVERSITY: 'University',
      PUBLIC_SQUARE: 'Public Square',
      LIBRARY: 'Library',
      OTHER: 'Other',
    };
    return labels[category] || category;
  };

  if (!visible || !hotspot) return null;

  // No offset needed - CSS handles all positioning relative to anchor point
  const getPixelPositionOffset = () => ({
    x: 0,
    y: 0,
  });

  const popupContent = (
    <div
      className="map-popup-content"
      data-theme={isDarkMode ? 'dark' : 'light'}
      style={{
        width: `${POPUP_WIDTH}px`,
        maxWidth: '92vw',
        maxHeight: `${MAX_POPUP_HEIGHT}px`,
        backgroundColor: colors.surface,
        borderRadius: '20px',
        boxShadow: isDarkMode ? '0 10px 40px rgba(0, 0, 0, 0.5)' : '0 10px 40px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        animation: 'slideUpFade 0.3s ease-out',
        zIndex: 100000,
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: '15px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Close Button - high z-index so it stays clickable above content */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          width: '36px',
          height: '36px',
          borderRadius: '18px',
          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 100002,
        }}
      >
        <Icon name="close" size={20} color={isDarkMode ? colors.text : '#fff'} />
      </div>

      {/* Photos Section - fixed at top, not scrollable (own state so auto-rotate doesn't re-render overlay) */}
      {(() => {
        const raw = hotspot.photos || [];
        const resolved = raw.map((p) => (p && p.startsWith('http') ? p : getHotspotPhotoUrl(p))).filter(Boolean);
        return resolved.length > 0 && (
          <HotspotPopupPhotoStrip photos={resolved} isMobile={isMobile} isDarkMode={isDarkMode} colors={colors} />
        );
      })()}

      {/* Scrollable body: header, address, description, reviews — keeps "See Full Page" visible at bottom */}
      <div
        className="popup-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Header */}
        <div style={{ padding: isMobile ? '12px' : '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10, marginBottom: '8px' }}>
            <div style={{ fontSize: isMobile ? 22 : 28 }}>{getCategoryIcon(hotspot.category)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 'bold', color: colors.text }}>
                {hotspot.name}
              </div>
              <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                <div
                  style={{
                    display: 'inline-block',
                    backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.3)' : '#FF9800',
                    color: isDarkMode ? '#FFB74D' : 'white',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                  }}
                >
                  {getCategoryLabel(hotspot.category)}
                </div>
                {hotspot.city && (
                  <div
                    style={{
                      display: 'inline-block',
                      backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.3)' : '#2196F3',
                      color: isDarkMode ? '#64B5F6' : 'white',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}
                  >
                    {hotspot.city}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '12px' }}>
            <Icon name="place" size={18} color={colors.textSecondary} />
            <div style={{ fontSize: '14px', color: colors.textSecondary, flex: 1 }}>
              {hotspot.address}
            </div>
          </div>

          {/* Description */}
          <div style={{ marginTop: '12px', fontSize: '14px', color: colors.textSecondary, lineHeight: '1.5' }}>
            {hotspot.description}
          </div>

          {/* Schedule & Activity Level */}
          <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {hotspot.schedule && (
              <div
                style={{
                  backgroundColor: colors.backgroundDark,
                  padding: '6px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  maxWidth: '100%',
                }}
              >
                <Icon name="schedule" size={14} color={colors.textSecondary} style={{ flexShrink: 0 }} />
                {hotspot.schedule.length > 80 || (hotspot.schedule.match(/;/g) || []).length >= 3
                  ? 'See full page for schedule'
                  : hotspot.schedule}
              </div>
            )}
            {hotspot.activityLevel && (
              <div
                style={{
                  backgroundColor: colors.backgroundDark,
                  padding: '6px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Icon name="trending-up" size={14} color={colors.textSecondary} />
                {hotspot.activityLevel}
              </div>
            )}
          </div>

          {/* Amenities */}
          {hotspot.amenities && hotspot.amenities.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: colors.textSecondary, marginBottom: '6px' }}>
                AMENITIES
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {hotspot.amenities.map((amenity, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.2)' : '#e8f5e9',
                      color: isDarkMode ? '#81C784' : '#2E7D32',
                      padding: '4px 10px',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: '500',
                    }}
                  >
                    {amenity}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Players Here Now */}
          {nearbyPlayers && nearbyPlayers.length > 0 && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '10px' }}>
                👥 {nearbyPlayers.length} {nearbyPlayers.length === 1 ? 'player' : 'players'} here now
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {nearbyPlayers.slice(0, 5).map((player) => (
                  <div
                    key={player.id}
                    onClick={() => onPlayerClick && onPlayerClick(player)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      backgroundColor: colors.backgroundDark,
                      borderRadius: '20px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.border)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundDark)}
                  >
                    {player.profilePictureUrl ? (
                      <img
                        src={player.profilePictureUrl}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '12px',
                          objectFit: 'cover',
                        }}
                        alt={player.username}
                      />
                    ) : (
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '12px',
                          backgroundColor: colors.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon name="person" size={14} color="white" />
                      </div>
                    )}
                    <div style={{ fontSize: '13px', fontWeight: '500', color: colors.text }}>
                      {player.username}
                    </div>
                  </div>
                ))}
                {nearbyPlayers.length > 5 && (
                  <div style={{ fontSize: '13px', color: colors.textSecondary, padding: '6px 10px' }}>
                    +{nearbyPlayers.length - 5} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reviews Section */}
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
                  Reviews
                </div>
                {reviews.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                    {renderStars(Math.round(avgRating))}
                    <div style={{ fontSize: '13px', color: colors.textSecondary, marginLeft: '4px' }}>
                      {avgRating.toFixed(1)} ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
                    </div>
                  </div>
                )}
              </div>
              <div
                onClick={() => setShowWriteReview(true)}
                style={{
                  backgroundColor: colors.primary,
                  color: 'white',
                  padding: '6px 14px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Write Review
              </div>
            </div>

            {loadingReviews ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <ActivityIndicator size="small" color={colors.primary} />
              </div>
            ) : reviews.length > 0 ? (
              <div style={{ position: 'relative' }}>
                {/* Reviews carousel container */}
                <div
                  style={{
                    overflow: 'hidden',
                    borderRadius: '10px',
                    backgroundColor: colors.backgroundDark,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      transition: 'transform 0.5s ease-in-out',
                      transform: `translateX(-${currentReviewIndex * 100}%)`,
                    }}
                  >
                    {reviews.map((review, index) => (
                      <div
                        key={review.id}
                        style={{
                          minWidth: '100%',
                          padding: '12px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          {review.user.profilePictureUrl ? (
                            <img
                              src={getProfilePictureUrl(review.user.profilePictureUrl, 'thumbnail') || review.user.profilePictureUrl}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '16px',
                                objectFit: 'cover',
                              }}
                              alt={review.user.username}
                            />
                          ) : (
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '16px',
                                backgroundColor: colors.primary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon name="person" size={16} color="white" />
                            </div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                              {review.user.username}
                            </div>
                            <div style={{ display: 'flex', gap: '2px', marginTop: '2px' }}>
                              {renderStars(review.rating)}
                            </div>
                          </div>
                        </div>
                        {review.comment && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: colors.textSecondary,
                              lineHeight: '1.5',
                              marginTop: '6px',
                            }}
                          >
                            {review.comment}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Review indicators */}
                {reviews.length > 1 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '6px',
                      marginTop: '10px',
                    }}
                  >
                    {reviews.map((_, index) => (
                      <div
                        key={index}
                        style={{
                          width: index === currentReviewIndex ? '20px' : '8px',
                          height: '8px',
                          borderRadius: '4px',
                          backgroundColor: index === currentReviewIndex ? colors.primary : (isDarkMode ? 'rgba(127, 176, 105, 0.4)' : 'rgba(148, 235, 152, 0.4)'),
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                        }}
                        onClick={() => setCurrentReviewIndex(index)}
                      />
                    ))}
                  </div>
                )}

                {/* See all reviews link */}
                {reviews.length > 0 && (
                  <div
                    style={{
                      textAlign: 'center',
                      color: colors.primary,
                      fontSize: '12px',
                      fontWeight: '600',
                      marginTop: '8px',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      onShowDetail && onShowDetail();
                    }}
                  >
                    See all {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'} →
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '13px', padding: '10px' }}>
                No reviews yet. Be the first to review!
              </div>
            )}
          </div>
        </div>
      </div>

      {/* See Full Page - fixed at bottom, always visible */}
      <div
        style={{
          flexShrink: 0,
          padding: '0 16px 16px 16px',
          paddingTop: '8px',
        }}
      >
        <div
            onClick={() => {
              // On desktop, use the modal overlay; on mobile, navigate
              const clubId = hotspot?.claimed_by || hotspot?.claimedBy || hotspot?.owner_id || hotspot?.owner?.id;
              if (isDesktop && onShowDetail) {
                onShowDetail();
              } else if (navigation) {
                navigation.navigate('HotspotDetail', { hotspotId: hotspot.id, hotspot, clubId });
                onClose();
              }
            }}
            style={{
              marginTop: '16px',
              padding: '14px',
              backgroundColor: colors.primary,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryLight;
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = isDarkMode ? '0 4px 12px rgba(127, 176, 105, 0.4)' : '0 4px 12px rgba(148, 235, 152, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Icon name="open-in-new" size={18} color="white" />
            <span style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
              See Full Page
            </span>
          </div>
        </div>

      {/* Pointer / Tail */}
      <div
        style={{
          position: 'absolute',
          bottom: '-12px',
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
          width: '24px',
          height: '24px',
          backgroundColor: colors.surface,
          boxShadow: isDarkMode ? '0 6px 18px rgba(0,0,0,0.3)' : '0 6px 18px rgba(0,0,0,0.12)',
          borderRadius: '4px',
          zIndex: -1,
        }}
      />

      <style>
        {`
          @keyframes slideUpFade {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          /* Custom scrollbar - matches app theme */
          .map-popup-content .popup-scroll {
            scrollbar-width: thin;
            scrollbar-color: #7FB069 #E9ECEF;
          }
          .map-popup-content[data-theme="dark"] .popup-scroll {
            scrollbar-color: #7FB069 #404040;
          }
          .map-popup-content .popup-scroll::-webkit-scrollbar {
            width: 8px;
          }
          .map-popup-content .popup-scroll::-webkit-scrollbar-track {
            background: #E9ECEF;
            border-radius: 4px;
            margin: 4px 0;
          }
          .map-popup-content .popup-scroll::-webkit-scrollbar-thumb {
            background: #7FB069;
            border-radius: 4px;
          }
          .map-popup-content .popup-scroll::-webkit-scrollbar-thumb:hover {
            background: #5C8A4D;
          }
          .map-popup-content[data-theme="dark"] .popup-scroll::-webkit-scrollbar-track {
            background: #404040;
          }
          .map-popup-content[data-theme="dark"] .popup-scroll::-webkit-scrollbar-thumb {
            background: #7FB069;
          }
          .map-popup-content[data-theme="dark"] .popup-scroll::-webkit-scrollbar-thumb:hover {
            background: #9DC183;
          }
        `}
      </style>
    </div>
  );

  const handleReviewSubmitted = async (newReview) => {
    // Load S3 URL for the new review's profile picture if needed
    let reviewWithUrl = newReview;
    if (newReview.user?.profilePictureUrl && !newReview.user.profilePictureUrl.startsWith('http')) {
      try {
        const url = await getProfilePictureUrl(newReview.user.profilePictureUrl);
        reviewWithUrl = {
          ...newReview,
          user: {
            ...newReview.user,
            profilePictureUrl: url,
          },
        };
      } catch (error) {
        console.error('Error loading profile picture for new review:', error);
      }
    }
    
    // Add the new review to the list and recalculate average
    const updatedReviews = [reviewWithUrl, ...reviews];
    setReviews(updatedReviews);
    
    const avg = updatedReviews.reduce((sum, r) => sum + r.rating, 0) / updatedReviews.length;
    setAvgRating(avg);
  };

  if (Platform.OS === 'web') {
    // During map drag on desktop: render popup in fixed viewport position so it doesn't shift (mobile uses overlay; pan is disabled when popup open)
    const useFixedOverlayDrag = visible && isMapDragging && frozenPopupViewport && typeof document !== 'undefined';
    if (useFixedOverlayDrag) {
      const ReactDOM = require('react-dom');
      return (
        <>
          {ReactDOM.createPortal(
            <div
              style={{
                position: 'fixed',
                left: frozenPopupViewport.x,
                top: frozenPopupViewport.y - 15,
                transform: 'translate(-50%, -100%)',
                zIndex: 100000,
                pointerEvents: 'auto',
              }}
            >
              {popupContent}
            </div>,
            document.body
          )}
          <WriteReviewModal
            visible={showWriteReview}
            onClose={() => setShowWriteReview(false)}
            hotspotId={hotspot.id}
            hotspotName={hotspot.name}
            userId={currentUserDbId}
            onReviewSubmitted={handleReviewSubmitted}
          />
        </>
      );
    }
    return (
      <>
        <OverlayViewF
          position={{ lat: hotspot.latitude, lng: hotspot.longitude }}
          mapPaneName={OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={getPixelPositionOffset}
        >
          {popupContent}
        </OverlayViewF>
        
        {/* Write Review Modal */}
        <WriteReviewModal
          visible={showWriteReview}
          onClose={() => setShowWriteReview(false)}
          hotspotId={hotspot.id}
          hotspotName={hotspot.name}
          userId={currentUserDbId}
          onReviewSubmitted={handleReviewSubmitted}
        />
      </>
    );
  }

  // For mobile (not implemented yet)
  return null;
};

const styles = StyleSheet.create({
  // Mobile styles would go here
});

export default HotspotPopup;

